import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Overlay } from 'react-bootstrap';
import CompactRoomMonitorCircle from './CompactRoomMonitorCircle';
import MonitorDoorCardV2 from './MonitorDoorCardV2';

export function usePrefersFineHover() {
    const [fine, setFine] = useState(() =>
        typeof window !== 'undefined' &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches
    );
    useEffect(() => {
        const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
        const sync = () => setFine(mq.matches);
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);
    return fine;
}

function isLeavingToHoverBridge(originEl, relatedTarget) {
    if (!relatedTarget || !originEl) return false;
    if (originEl.contains(relatedTarget)) return true;
    return !!relatedTarget.closest?.('.compact-door-hover-layer');
}

/**
 * Compact circles: full door card in a popover on hover (click/tap toggles when hover is unavailable).
 */
export default function CompactRoomMonitorCellWithDoorHover({
    room,
    badgeDisplayMode,
    onBadgeClick,
    onConfirmationClick,
    footerSlotHeightPx = null,
    /** Popover stacks above modals (e.g. bulk matcher at z-index 2100); default matches Bootstrap popover. */
    overlayRootZIndex = 1070
}) {
    const popperTargetRef = useRef(null);
    const circleWrapperRef = useRef(null);
    const [show, setShow] = useState(false);
    const fineHover = usePrefersFineHover();

    const hideNow = useCallback(() => {
        setShow(false);
    }, []);

    const showPreview = useCallback(() => {
        setShow(true);
    }, []);

    const onCircleMouseLeave = useCallback(
        (e) => {
            const rt = e.relatedTarget;
            if (isLeavingToHoverBridge(e.currentTarget, rt)) return;
            hideNow();
        },
        [hideNow]
    );

    const onOverlayMouseLeave = useCallback(
        (e) => {
            const rt = e.relatedTarget;
            if (circleWrapperRef.current && isLeavingToHoverBridge(circleWrapperRef.current, rt)) return;
            hideNow();
        },
        [hideNow]
    );

    const onCircleTapToggle = useCallback(() => {
        setShow((v) => !v);
    }, []);

    return (
        <>
            <span
                ref={circleWrapperRef}
                className="d-inline-block"
                style={{ outline: 'none' }}
                tabIndex={0}
                onMouseEnter={showPreview}
                onMouseLeave={onCircleMouseLeave}
                onFocus={showPreview}
                onBlur={(e) => {
                    const rt = e.relatedTarget;
                    if (
                        rt &&
                        (e.currentTarget.contains(rt) || rt.closest?.('.compact-door-hover-layer'))
                    ) {
                        return;
                    }
                    hideNow();
                }}
            >
                <CompactRoomMonitorCircle
                    data={room}
                    roomNumber={room.roomNumber}
                    badgeDisplayMode={badgeDisplayMode}
                    onBadgeClick={onBadgeClick}
                    onCircleClick={fineHover ? undefined : onCircleTapToggle}
                    popperTargetRef={popperTargetRef}
                    footerSlotHeightPx={footerSlotHeightPx}
                />
            </span>
            <Overlay
                show={show}
                target={popperTargetRef}
                placement="top"
                flip
                transition={null}
                offset={[0, 4]}
                rootClose
                rootCloseEvent="mousedown"
                container={typeof document !== 'undefined' ? document.body : undefined}
                containerPadding={8}
                popperConfig={{
                    modifiers: [
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['bottom']
                            }
                        }
                    ]
                }}
                onHide={hideNow}
            >
                {(overlayProps) => (
                    <div
                        {...overlayProps}
                        style={{
                            zIndex: overlayRootZIndex,
                            ...overlayProps.style
                        }}
                        className={['compact-door-hover-layer', overlayProps.className]
                            .filter(Boolean)
                            .join(' ')}
                        onMouseEnter={(e) => {
                            showPreview();
                            overlayProps.onMouseEnter?.(e);
                        }}
                        onMouseLeave={(e) => {
                            onOverlayMouseLeave(e);
                            overlayProps.onMouseLeave?.(e);
                        }}
                    >
                        <div className="compact-door-popover-door-wrap">
                            <MonitorDoorCardV2
                                data={room}
                                key={room.roomNumber}
                                roomNumber={room.roomNumber}
                                status={room.status}
                                statusColor={room.statusColor}
                                isDnd={room.isDnd}
                                show_icon={room.show_icon}
                                icon_blink_interval={room.icon_blink_interval}
                                isMonitor={true}
                                onConfirmationClick={onConfirmationClick}
                                badgeDisplayMode={badgeDisplayMode}
                                compactDoorEmbed
                            />
                        </div>
                    </div>
                )}
            </Overlay>
        </>
    );
}
